import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from "recharts";
import { cn } from "@/lib/utils";

/**
 * Genera dati per un loop flusso-volume di spirometria
 * Il loop mostra l'espirazione (sopra) e l'inspirazione (sotto)
 */
const generateFlowVolumeData = (fev1 = 3.5, fvc = 4.5, pef = 9.5) => {
  const data = [];
  const totalPoints = 100;
  
  // Normalize values based on provided parameters
  const peakFlow = pef;
  const totalVolume = fvc;
  const fev1Ratio = fev1 / fvc;
  
  // Generate expiratory curve (positive flow, going right)
  for (let i = 0; i <= totalPoints / 2; i++) {
    const t = i / (totalPoints / 2); // 0 to 1
    const volume = t * totalVolume;
    
    // Expiratory flow curve - peaks early then declines
    let flow;
    if (t < 0.1) {
      // Rapid rise to peak
      flow = peakFlow * (t / 0.1);
    } else {
      // Exponential decay from peak
      const decay = (t - 0.1) / 0.9;
      flow = peakFlow * Math.exp(-2.5 * decay);
    }
    
    data.push({
      volume: volume,
      expFlow: flow,
      inspFlow: null,
      type: 'exp'
    });
  }
  
  // Generate inspiratory curve (negative flow, going back left)
  for (let i = totalPoints / 2; i >= 0; i--) {
    const t = i / (totalPoints / 2); // 1 to 0
    const volume = t * totalVolume;
    
    // Inspiratory flow - more uniform, slightly peaked in middle
    const midPoint = 0.5;
    const distance = Math.abs(t - midPoint);
    const maxInspFlow = peakFlow * 0.7; // Inspiratory flow is typically less than expiratory
    const flow = -maxInspFlow * (1 - distance * 1.2);
    
    data.push({
      volume: volume,
      expFlow: null,
      inspFlow: flow,
      type: 'insp'
    });
  }
  
  return data;
};

/**
 * Genera dati per curva volume-tempo
 */
const generateVolumeTimeData = (fev1 = 3.5, fvc = 4.5) => {
  const data = [];
  const duration = 6; // 6 secondi
  const samplingRate = 20;
  
  for (let i = 0; i <= duration * samplingRate; i++) {
    const t = i / samplingRate; // tempo in secondi
    
    // Volume espiratorio - curva esponenziale
    // FEV1 raggiunto a 1 secondo, FVC raggiunto a ~3-4 secondi
    const tau = 1.2; // costante di tempo
    const volume = fvc * (1 - Math.exp(-t / tau));
    
    data.push({
      time: t,
      volume: Math.min(volume, fvc),
      fev1Mark: t === 1 ? fev1 : null
    });
  }
  
  return data;
};

/**
 * Componente Loop Flusso-Volume
 */
export const FlowVolumeLoop = ({ 
  fev1 = 3.5, 
  fvc = 4.5, 
  pef = 9.5,
  height = 250,
  className
}) => {
  const data = useMemo(() => generateFlowVolumeData(fev1, fvc, pef), [fev1, fvc, pef]);
  
  return (
    <div className={cn("w-full", className)}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart 
          data={data} 
          margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="hsl(var(--border))" 
            strokeOpacity={0.5}
          />
          <XAxis 
            dataKey="volume" 
            type="number"
            domain={[0, Math.ceil(fvc) + 0.5]}
            label={{ value: 'Volume (L)', position: 'bottom', offset: 0 }}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <YAxis 
            domain={[-Math.ceil(pef * 0.8), Math.ceil(pef) + 1]}
            label={{ value: 'Flusso (L/s)', angle: -90, position: 'insideLeft', offset: 10 }}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
          <Line 
            type="monotone" 
            dataKey="expFlow" 
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            dot={false}
            connectNulls={false}
          />
          <Line 
            type="monotone" 
            dataKey="inspFlow" 
            stroke="hsl(var(--info))"
            strokeWidth={2}
            dot={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-2 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-primary" />
          <span className="text-muted-foreground">Espirazione</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-info" />
          <span className="text-muted-foreground">Inspirazione</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Componente Curva Volume-Tempo
 */
export const VolumeTimeCurve = ({ 
  fev1 = 3.5, 
  fvc = 4.5, 
  height = 200,
  className
}) => {
  const data = useMemo(() => generateVolumeTimeData(fev1, fvc), [fev1, fvc]);
  
  return (
    <div className={cn("w-full", className)}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart 
          data={data} 
          margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="hsl(var(--border))" 
            strokeOpacity={0.5}
          />
          <XAxis 
            dataKey="time" 
            type="number"
            domain={[0, 6]}
            label={{ value: 'Tempo (s)', position: 'bottom', offset: 0 }}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <YAxis 
            domain={[0, Math.ceil(fvc) + 0.5]}
            label={{ value: 'Volume (L)', angle: -90, position: 'insideLeft', offset: 10 }}
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          {/* FEV1 marker at 1 second */}
          <ReferenceLine x={1} stroke="hsl(var(--warning))" strokeDasharray="5 5" />
          <ReferenceLine y={fev1} stroke="hsl(var(--warning))" strokeDasharray="5 5" />
          <Area 
            type="monotone" 
            dataKey="volume" 
            stroke="hsl(var(--primary))"
            fill="hsl(var(--primary))"
            fillOpacity={0.1}
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Componente Monitor Spirometria completo
 */
export const SpirometryMonitor = ({
  fev1 = 3.5,
  fvc = 4.5,
  fev1Fvc = 78,
  pef = 9.5,
  className
}) => {
  // Calculate predicted percentages (mock - in real app would use patient data)
  const fev1Predicted = 85; // % of predicted
  const fvcPredicted = 90;
  
  const getStatusColor = (value) => {
    if (value >= 80) return "text-success";
    if (value >= 60) return "text-warning";
    return "text-destructive";
  };
  
  return (
    <div className={cn("rounded-xl bg-slate-950 p-4 space-y-4", className)}>
      {/* Header con parametri */}
      <div className="grid grid-cols-4 gap-4">
        <div className="text-center p-3 rounded-lg bg-slate-900">
          <p className="text-xs text-slate-400 mb-1">FEV1</p>
          <p className="text-2xl font-bold text-primary font-mono">{fev1.toFixed(2)}</p>
          <p className="text-xs text-slate-500">L</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-slate-900">
          <p className="text-xs text-slate-400 mb-1">FVC</p>
          <p className="text-2xl font-bold text-info font-mono">{fvc.toFixed(2)}</p>
          <p className="text-xs text-slate-500">L</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-slate-900">
          <p className="text-xs text-slate-400 mb-1">FEV1/FVC</p>
          <p className={cn("text-2xl font-bold font-mono", getStatusColor(fev1Fvc))}>{fev1Fvc}</p>
          <p className="text-xs text-slate-500">%</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-slate-900">
          <p className="text-xs text-slate-400 mb-1">PEF</p>
          <p className="text-2xl font-bold text-amber-400 font-mono">{pef.toFixed(1)}</p>
          <p className="text-xs text-slate-500">L/s</p>
        </div>
      </div>
      
      {/* Flow-Volume Loop */}
      <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
        <p className="text-xs text-slate-400 mb-2 text-center">Loop Flusso-Volume</p>
        <FlowVolumeLoop 
          fev1={fev1}
          fvc={fvc}
          pef={pef}
          height={220}
        />
      </div>
      
      {/* Interpretation */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="p-3 rounded-lg bg-slate-900 border border-slate-800">
          <p className="text-xs text-slate-400 mb-1">FEV1 % Predetto</p>
          <div className="flex items-center gap-2">
            <span className={cn("text-lg font-bold", getStatusColor(fev1Predicted))}>{fev1Predicted}%</span>
            <span className="text-xs text-slate-500">({fev1Predicted >= 80 ? 'Normale' : fev1Predicted >= 60 ? 'Lieve riduzione' : 'Ridotto'})</span>
          </div>
        </div>
        <div className="p-3 rounded-lg bg-slate-900 border border-slate-800">
          <p className="text-xs text-slate-400 mb-1">FVC % Predetto</p>
          <div className="flex items-center gap-2">
            <span className={cn("text-lg font-bold", getStatusColor(fvcPredicted))}>{fvcPredicted}%</span>
            <span className="text-xs text-slate-500">({fvcPredicted >= 80 ? 'Normale' : fvcPredicted >= 60 ? 'Lieve riduzione' : 'Ridotto'})</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpirometryMonitor;
